import { Link } from 'react-router';
import { Card, CardContent, CardDescription, CardFooter, CardTitle } from '@/components/ui/card';
import { themeColorLinkClassNames } from '@/components/ui/utils';
import { getArticleFeaturedImage, getArticleImageUrl, type ArticlesListItem } from '@/services/StrapiService';
import { articlePath, descriptionFromArticle, titleFromArticle } from '@/pages/ResourceCenter/resourceCenter';
import { cn } from '@/lib/utils';
import 'material-symbols';

/**
 * A fixed height per breakpoint rather than however tall the contents happen to be, so a carousel
 * of them lines up and the scaled neighbors sit squarely either side.
 */
const COMPACT_HEIGHT_CLASS = 'h-[300px] sm:h-[344px]';

export interface ArticleSummaryCardProps {
    item: ArticlesListItem;
    className?: string;
    /** `compact` drops the description and moves the call to action to the trailing edge. */
    variant?: 'default' | 'compact';
}

/**
 * A single article summary, linking to its detail page. Distinct from `ResourceCard`, which renders
 * the normalized `Resource` shape the Resource Center grid works in.
 */
export const ArticleSummaryCard = ({ item, className, variant = 'default' }: ArticleSummaryCardProps) => {
    const isCompact = variant === 'compact';
    const data = item.data ?? {};
    const path = articlePath(item.slug);
    const imageUrl = getArticleImageUrl(data);
    const featured = getArticleFeaturedImage(data);
    const title = titleFromArticle(item);
    const imgAlt = featured?.alternativeText?.trim() || `${title} — featured image`;
    const description = descriptionFromArticle(item);

    return (
        <article className={cn('h-full min-w-0', className)}>
            <Link
                to={path}
                className={cn(
                    'group block h-full rounded-xl focus-visible:outline-none',
                    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                )}
            >
                <Card
                    withBorder
                    noPadding
                    shadow="sm"
                    cursorPointer
                    className={cn(
                        'h-full overflow-hidden transition-colors',
                        'light:hover:bg-surface-dark dark:hover:bg-gz-blue-700',
                        // In dark mode the compact card carries its own ground, not the card surface.
                        isCompact && cn(COMPACT_HEIGHT_CLASS, 'dark:bg-gz-bluebell-500'),
                    )}
                >
                    {/* The compact card floats its image inside the card rather than bleeding it
                        to the corners. The wrapper carries the inset and the image its own radius. */}
                    <div className={cn(isCompact && 'min-h-0 flex-1 p-4 pb-0')}>
                        <div className={cn('relative aspect-[4/3] w-full bg-muted overflow-hidden', isCompact && 'aspect-auto h-full rounded-xl')}>
                            {imageUrl
                                ? (
                                        <img
                                            src={imageUrl}
                                            alt={imgAlt}
                                            className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                        />
                                    )
                                : (
                                        <div
                                            className="absolute inset-0 flex items-center justify-center text-muted-foreground type-body-default"
                                            aria-hidden
                                        >
                                            No image
                                        </div>
                                    )}
                        </div>
                    </div>
                    <CardContent className={cn('flex flex-col gap-3 pt-6 pb-2 px-6 flex-1', isCompact && 'flex-none pt-4 px-4')}>
                        <CardTitle
                            className={cn(
                                'type-headline-xsmall light:text-foreground dark:text-foreground',
                                'line-clamp-2 leading-snug group-hover:text-primary transition-colors',
                            )}
                            title={title}
                        >
                            {title}
                        </CardTitle>
                        {description && !isCompact
                            ? (
                                    <CardDescription className="type-body-default line-clamp-3">
                                        {description}
                                    </CardDescription>
                                )
                            : null}
                    </CardContent>
                    <CardFooter className={cn('mt-auto pt-2 pb-6 px-6 border-0', isCompact && 'justify-end pb-4 px-4')}>
                        <span
                            className={cn(
                                'inline-flex items-center gap-1 type-body-default font-medium',
                                'group-hover:gap-2 transition-all',
                                themeColorLinkClassNames,
                                'light:group-hover:text-[var(--gz-marine-700)] dark:group-hover:text-[var(--gz-marine-400)]',
                            )}
                        >
                            Read more
                            <span className="material-symbols-rounded text-lg leading-none" aria-hidden>
                                arrow_forward
                            </span>
                        </span>
                    </CardFooter>
                </Card>
            </Link>
        </article>
    );
};
